import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import {
  AuthProvider,
  AuthGuard,
  AdminGuard,
  PermissionGuard,
  AccessDenied,
  MainLayout,
  MessagingStandaloneLayout,
  LoginPage,
  ForgotPasswordPage,
  AcceptInvitePage,
  ResetPasswordPage,
  DashboardPage,
  TicketsListPage,
  TicketDetailPage,
  TicketsArchivePage,
  NotificationsPage,
  SearchResultsPage,
  LogsPage,
  SettingsPage,
  CabinetSettingsTab,
  TeamSettingsTab,
  AgendaSettingsTab,
  AgentSettingsTab,
  CategoriesTab,
  TaskTemplatesTab,
  DossierSettingsTab,
  DocTemplatesTab,
  GoogleIntegrationsTab,
  PlaceholderPage,
  NotFoundPage,
  ProfilePage,
  LawyerProfilePage,
  TasksPage,
  DesignSystemPage,
  DossiersListPage,
  DossierDetailPage,
  DossiersKanbanPage,
  ContactsListPage,
  ContactDetailPage,
  BillingListPage,
  BillingDetailPage,
  TimesheetPage,
  CalendarPage,
  StatsPage,
  GmailInboxPage,
  EBarreauPage,
  ParapheurPage,
  CircuitReviewPage,
  CabinetLibraryPage,
  FilesListPage,
  MessagingPage,
  ClientGuard,
  PortalLayout,
  PortalDossiersListPage,
  PortalDossierDetailPage,
  PortalProfilePage,
} from '@cime/shared';
import {
  Sparkles,
} from 'lucide-react';

// Root layout: mounts AuthProvider inside the router so useNavigate is available
function RootLayout() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

export const router = createBrowserRouter([
  {
    // Root layout wraps everything so AuthProvider has router context
    element: <RootLayout />,
    children: [
      // ── Public routes ──────────────────────────────────────────
      {
        path: '/login',
        element: <LoginPage />,
      },
      {
        path: '/forgot-password',
        element: <ForgotPasswordPage />,
      },
      {
        path: '/accept-invite',
        element: <AcceptInvitePage />,
        handle: { breadcrumb: 'Invitation' },
      },
      {
        path: '/reset-password',
        element: <ResetPasswordPage />,
        handle: { breadcrumb: 'Réinitialisation' },
      },
      {
        path: '/404',
        element: <NotFoundPage />,
      },

      // ── Protected routes (AuthGuard) ───────────────────────────
      {
        element: <AuthGuard />,
        children: [
          // Redirect root to dashboard
          {
            path: '/',
            element: <Navigate to="/dashboard" replace />,
          },

          {
            element: <MessagingStandaloneLayout />,
            children: [
              {
                path: '/messagerie',
                element: (
                  <PermissionGuard feature="messaging" fallback={<AccessDenied />}>
                    <MessagingPage />
                  </PermissionGuard>
                ),
              },
              {
                path: '/messagerie/:channelId',
                element: (
                  <PermissionGuard feature="messaging" fallback={<AccessDenied />}>
                    <MessagingPage />
                  </PermissionGuard>
                ),
              },
            ],
          },

          // MainLayout wrapper
          {
            element: <MainLayout />,
            children: [
              {
                path: '/dashboard',
                element: <DashboardPage />,
                handle: { breadcrumb: 'Tableau de bord' },
              },
              {
                path: '/tickets',
                element: (
                  <PermissionGuard feature="tickets_view" fallback={<AccessDenied />}>
                    <TicketsListPage />
                  </PermissionGuard>
                ),
                handle: { breadcrumb: 'Tickets' },
              },
              {
                path: '/tickets/archives',
                element: (
                  <PermissionGuard feature="tickets_view" fallback={<AccessDenied />}>
                    <TicketsArchivePage />
                  </PermissionGuard>
                ),
                handle: { breadcrumb: 'Archives' },
              },
              {
                path: '/tickets/:id',
                element: (
                  <PermissionGuard feature="tickets_view" fallback={<AccessDenied />}>
                    <TicketDetailPage />
                  </PermissionGuard>
                ),
                handle: { breadcrumb: 'Détail' },
              },
              {
                path: '/dossiers',
                element: (
                  <PermissionGuard feature="dossiers_view" fallback={<AccessDenied />}>
                    <DossiersListPage />
                  </PermissionGuard>
                ),
                handle: { breadcrumb: 'Dossiers' },
              },
              {
                path: '/dossiers/kanban',
                element: (
                  <PermissionGuard feature="dossiers_view" fallback={<AccessDenied />}>
                    <DossiersKanbanPage />
                  </PermissionGuard>
                ),
                handle: { breadcrumb: 'Kanban' },
              },
              {
                path: '/dossiers/:id/*',
                element: (
                  <PermissionGuard feature="dossiers_view" fallback={<AccessDenied />}>
                    <DossierDetailPage />
                  </PermissionGuard>
                ),
                handle: { breadcrumb: 'Détail' },
              },
              {
                path: '/contacts',
                element: (
                  <PermissionGuard feature="contacts_view" fallback={<AccessDenied />}>
                    <ContactsListPage />
                  </PermissionGuard>
                ),
                handle: { breadcrumb: 'Répertoire' },
              },
              {
                path: '/contacts/:id/*',
                element: (
                  <PermissionGuard feature="contacts_view" fallback={<AccessDenied />}>
                    <ContactDetailPage />
                  </PermissionGuard>
                ),
                handle: { breadcrumb: 'Contact' },
              },
              {
                path: '/taches',
                element: (
                  <PermissionGuard feature="tasks_view" fallback={<AccessDenied />}>
                    <TasksPage />
                  </PermissionGuard>
                ),
                handle: { breadcrumb: 'Tâches' },
              },
              {
                path: '/agenda',
                element: <Navigate to="/dossiers" replace />,
              },
              {
                path: '/feuille-de-temps',
                element: (
                  <PermissionGuard feature="timesheet_view" fallback={<AccessDenied />}>
                    <TimesheetPage />
                  </PermissionGuard>
                ),
                handle: { breadcrumb: 'Feuille de temps' },
              },
              {
                path: '/calendrier',
                element: (
                  <PermissionGuard feature="calendar_view" fallback={<AccessDenied />}>
                    <CalendarPage />
                  </PermissionGuard>
                ),
                handle: { breadcrumb: 'Calendrier' },
              },
              {
                path: '/gmail-inbox',
                element: (
                  <PermissionGuard feature="gmail_inbox_view" fallback={<AccessDenied />}>
                    <GmailInboxPage />
                  </PermissionGuard>
                ),
                handle: { breadcrumb: 'Boite Gmail' },
              },
              {
                path: '/e-barreau',
                element: (
                  <PermissionGuard feature="ebarreau_view" fallback={<AccessDenied />}>
                    <EBarreauPage />
                  </PermissionGuard>
                ),
                handle: { breadcrumb: 'e-Barreau' },
              },
              {
                path: '/parapheur',
                element: (
                  <PermissionGuard feature="signature_my_parapheur" fallback={<AccessDenied />}>
                    <ParapheurPage />
                  </PermissionGuard>
                ),
                handle: { breadcrumb: 'Parapheur' },
              },
              {
                path: '/parapheur/:circuitId',
                element: (
                  <PermissionGuard feature="signature_my_parapheur" fallback={<AccessDenied />}>
                    <CircuitReviewPage />
                  </PermissionGuard>
                ),
                handle: { breadcrumb: 'Document' },
              },
              {
                path: '/fichiers',
                element: (
                  <PermissionGuard feature="files_view" fallback={<AccessDenied />}>
                    <FilesListPage />
                  </PermissionGuard>
                ),
                handle: { breadcrumb: 'Fichiers' },
              },
              {
                path: '/bibliotheque',
                element: (
                  <PermissionGuard feature="cabinet_library" fallback={<AccessDenied />}>
                    <CabinetLibraryPage />
                  </PermissionGuard>
                ),
                handle: { breadcrumb: 'Bibliothèque' },
              },
              {
                path: '/facturation',
                element: (
                  <PermissionGuard feature="billing_view" fallback={<AccessDenied />}>
                    <BillingListPage />
                  </PermissionGuard>
                ),
                handle: { breadcrumb: 'Facturation' },
              },
              {
                path: '/facturation/:id',
                element: (
                  <PermissionGuard feature="billing_view" fallback={<AccessDenied />}>
                    <BillingDetailPage />
                  </PermissionGuard>
                ),
                handle: { breadcrumb: 'Document' },
              },
              {
                path: '/assistant-ia',
                element: (
                  <PlaceholderPage
                    icon={Sparkles}
                    title="Assistant IA"
                    description="L'assistant juridique IA avec RAG documentaire et analyse de dossiers sera disponible en Phase 4."
                  />
                ),
                handle: { breadcrumb: 'Assistant IA' },
              },
              {
                path: '/profil',
                element: <ProfilePage />,
                handle: { breadcrumb: 'Mon profil' },
              },
              {
                path: '/equipe/:collaboratorId',
                element: <LawyerProfilePage />,
                handle: { breadcrumb: 'Profil' },
              },
              {
                path: '/recherche',
                element: (
                  <PermissionGuard feature="search_view" fallback={<AccessDenied />}>
                    <SearchResultsPage />
                  </PermissionGuard>
                ),
                handle: { breadcrumb: 'Recherche' },
              },
              {
                path: '/notifications',
                element: <NotificationsPage />,
                handle: { breadcrumb: 'Notifications' },
              },
              {
                path: '/logs',
                element: (
                  <PermissionGuard feature="logs_view" fallback={<AccessDenied />}>
                    <LogsPage />
                  </PermissionGuard>
                ),
                handle: { breadcrumb: 'Logs' },
              },
              {
                path: '/design-system',
                element: (
                  <PermissionGuard feature="design_system" fallback={<AccessDenied />}>
                    <DesignSystemPage />
                  </PermissionGuard>
                ),
                handle: { breadcrumb: 'Design System' },
              },

              {
                path: '/stats',
                element: (
                  <PermissionGuard feature="stats_cabinet_view" fallback={<AccessDenied />}>
                    <StatsPage />
                  </PermissionGuard>
                ),
                handle: { breadcrumb: 'Statistiques' },
              },

              // Admin-only routes — coarse gate via AdminGuard,
              // fine-grained per-tab via PermissionGuard with in-page fallback
              {
                element: <AdminGuard />,
                children: [
                  {
                    path: '/parametres',
                    element: <SettingsPage />,
                    handle: { breadcrumb: 'Paramètres' },
                    children: [
                      {
                        index: true,
                        element: <Navigate to="cabinet" replace />,
                      },
                      {
                        path: 'cabinet',
                        element: <CabinetSettingsTab />,
                        handle: { breadcrumb: 'Cabinet' },
                      },
                      {
                        path: 'branding',
                        element: <Navigate to="/parametres/cabinet" replace />,
                      },
                      {
                        path: 'equipe',
                        element: <TeamSettingsTab />,
                        handle: { breadcrumb: 'Équipe' },
                      },
                      {
                        path: 'permissions',
                        element: <Navigate to="/parametres/equipe" replace />,
                      },
                      {
                        path: 'demandes',
                        element: (
                          <PermissionGuard
                            feature="settings_categories"
                            fallback={<AccessDenied />}
                          >
                            <CategoriesTab />
                          </PermissionGuard>
                        ),
                        handle: { breadcrumb: 'Demandes' },
                      },
                      {
                        path: 'categories',
                        element: <Navigate to="/parametres/demandes" replace />,
                      },
                      {
                        path: 'tags',
                        element: <Navigate to="/parametres/taches" replace />,
                      },
                      {
                        path: 'taches',
                        element: (
                          <PermissionGuard
                            feature="settings_task_templates"
                            fallback={<AccessDenied />}
                          >
                            <TaskTemplatesTab />
                          </PermissionGuard>
                        ),
                        handle: { breadcrumb: 'Tâches' },
                      },
                      {
                        path: 'dossiers',
                        element: (
                          <PermissionGuard
                            feature="settings_dossiers"
                            fallback={<AccessDenied />}
                          >
                            <DossierSettingsTab />
                          </PermissionGuard>
                        ),
                        handle: { breadcrumb: 'Dossiers' },
                      },
                      {
                        path: 'agenda',
                        element: (
                          <PermissionGuard
                            feature="settings_agenda"
                            fallback={<AccessDenied />}
                          >
                            <AgendaSettingsTab />
                          </PermissionGuard>
                        ),
                        handle: { breadcrumb: 'Agenda' },
                      },
                      {
                        path: 'taches-types',
                        element: <Navigate to="/parametres/taches" replace />,
                      },
                      {
                        path: 'modeles',
                        element: (
                          <PermissionGuard
                            feature="settings_doc_templates"
                            fallback={<AccessDenied />}
                          >
                            <DocTemplatesTab />
                          </PermissionGuard>
                        ),
                        handle: { breadcrumb: 'Modèles' },
                      },
                      {
                        path: 'integrations',
                        element: (
                          <PermissionGuard
                            feature="calendar_google_sync"
                            fallback={<AccessDenied />}
                          >
                            <GoogleIntegrationsTab />
                          </PermissionGuard>
                        ),
                        handle: { breadcrumb: 'Intégrations' },
                      },
                      {
                        path: 'agent',
                        element: (
                          <PermissionGuard
                            feature="settings_ai_agent"
                            fallback={<AccessDenied />}
                          >
                            <AgentSettingsTab />
                          </PermissionGuard>
                        ),
                        handle: { breadcrumb: 'Agent IA' },
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },

      // ── Portail client (ClientGuard) ───────────────────────────
      {
        path: '/portail',
        element: <ClientGuard />,
        children: [
          {
            element: <PortalLayout />,
            children: [
              {
                index: true,
                element: <Navigate to="/portail/dossiers" replace />,
              },
              {
                path: 'dossiers',
                element: <PortalDossiersListPage />,
              },
              {
                path: 'dossiers/:id',
                element: <PortalDossierDetailPage />,
              },
              {
                path: 'profil',
                element: <PortalProfilePage />,
              },
            ],
          },
        ],
      },

      // ── Catch-all ──────────────────────────────────────────────
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);
